/**
 * <CancelFeesPage> — étape 3/5 : les frais (page)
 *
 * Combien, pourquoi, et à partir de quand. Deux suites possibles, et c'est le view-model qui
 * tranche : avec des frais, l'étape 4 les prélève ; sans frais, la confirmation suffit et le
 * parcours saute au revoir.
 *
 * La page ne calcule ni le montant, ni la date, ni la condition : `fees` arrive entièrement
 * écrit, et `confirmVisible` est ouvert puis refermé par l'appelant.
 */
import React from "react"

import { type UICancelFees } from "../../../ports/primary/ui/types"
import { Button } from "../../atoms/Buttons/Button"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { AlertCard } from "../../molecules/AlertCard"
import { CancelFeesCard } from "../../molecules/CancelFeesCard"
import { ConfirmDialog } from "../../molecules/ConfirmDialog"
import { CancelFlowStep } from "../../templates/CancelFlowStep"
import { CancelFeesSkeleton } from "./CancelFeesSkeleton"

/** Tant que les frais ne sont pas connus, l'étape se nomme par ce qu'elle va montrer. */
const FALLBACK_STEP_TITLE = "Frais de résiliation"
const APPOINTMENT_LABEL = "Prendre RDV avec un conseiller"
const CONTINUE_LABEL = "Poursuivre la résiliation"
const CONFIRM_TITLE = "Confirmer la résiliation ?"
const CONFIRM_LABEL = "Confirmer la résiliation"

export type CancelFeesPageProps = {
    /** `null` pendant le chargement — la coque garde la tête d'étape. */
    fees: UICancelFees | null
    loading?: boolean
    error?: unknown
    onRetry?: () => void
    /** Ouvre la prise de rendez-vous. */
    onBookAppointment: () => void
    /**
     * Poursuivre : avec des frais, la page suivante ; sans frais, l'appelant ouvre la
     * confirmation ci-dessous.
     */
    onContinue: () => void
    /** La résiliation sans frais est en cours d'envoi. */
    submitting?: boolean
    // ── Confirmation (frais nuls) — ouverte et fermée par l'appelant.
    confirmVisible?: boolean
    /** La phrase datée de la confirmation — « Cette action mettra fin à votre abonnement le … ». */
    confirmMessage?: string
    onConfirm?: () => void
    onCancelConfirm?: () => void
    onBack?: () => void
    onClose?: () => void
    testID?: string
}

export function CancelFeesPage(props: CancelFeesPageProps) {
    const {
        fees,
        loading = false,
        error,
        onRetry,
        onBookAppointment,
        onContinue,
        submitting = false,
        confirmVisible = false,
        confirmMessage,
        onConfirm,
        onCancelConfirm,
        onBack,
        onClose,
        testID,
    } = props

    return (
        <CancelFlowStep
            testID={testID}
            stepTitle={fees?.title ?? FALLBACK_STEP_TITLE}
            step={3}
            onBack={onBack}
            onClose={onClose}
            loading={loading}
            skeleton={<CancelFeesSkeleton />}
            error={error}
            onRetry={onRetry}
        >
            {fees != null && (
                <Stack gap="4">
                    <Text size="text-base" color="text-muted">
                        {fees.lead}
                    </Text>

                    <CancelFeesCard
                        hasFees={fees.hasFees}
                        amountLabel={fees.amountLabel}
                        detail={fees.detail}
                        dateLine={fees.dateLine}
                        creditLines={fees.creditLines}
                    />

                    <AlertCard
                        severity="info"
                        message={
                            <Stack gap="1">
                                <Text size="text-md" weight="bold" color="primary">
                                    {fees.effectTitle}
                                </Text>
                                <Text size="text-sm" color="text-primary">
                                    {fees.effectText}
                                </Text>
                            </Stack>
                        }
                    />

                    <Button
                        label={APPOINTMENT_LABEL}
                        iconName="calendar-check"
                        fullWidth
                        disabled={submitting}
                        onPress={onBookAppointment}
                    />
                    <TextButton
                        label={CONTINUE_LABEL}
                        color="text-muted"
                        size="sm"
                        fullWidth
                        loading={submitting}
                        onPress={onContinue}
                    />
                </Stack>
            )}

            {onConfirm != null && onCancelConfirm != null && (
                <ConfirmDialog
                    visible={confirmVisible}
                    title={CONFIRM_TITLE}
                    message={confirmMessage}
                    confirmLabel={CONFIRM_LABEL}
                    onConfirm={onConfirm}
                    onCancel={onCancelConfirm}
                />
            )}
        </CancelFlowStep>
    )
}
